import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Plus, ChevronRight, UserCog, ShieldCheck, Copy, Eye, EyeOff } from "lucide-react";

const PERM_GROUPS = {
  Staff: ["view_staff", "edit_staff"],
  Orders: ["view_orders", "create_orders", "edit_orders", "manage_suppliers", "manage_order_templates"],
  Recipes: ["view_recipes", "manage_recipes"],
  Menus: ["view_menus", "manage_menus"],
  Temperature: ["view_temperature", "create_temperature", "edit_temperature"],
  Cleaning: ["view_cleaning", "manage_cleaning", "complete_cleaning"],
  Prep: ["view_prep", "manage_prep", "complete_prep"],
  Checklists: ["view_checklists", "manage_checklists", "complete_checklists"],
  Other: ["view_activity", "view_reports", "manage_settings"],
};

export default function Staff() {
  const { has } = useAuth();
  const [staff, setStaff] = useState([]);
  const [modal, setModal] = useState(false);
  const [detail, setDetail] = useState(null);
  const [invite, setInvite] = useState(null);

  const load = async () => { try { setStaff((await api.get("/staff")).data); } catch { toast.error("Could not load staff"); } };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Manage Staff" subtitle="Personal logins power accountability on every entry." testid="staff-header"
        action={has("edit_staff") && <Button className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="add-staff-btn" onClick={() => setModal(true)}><Plus size={18} className="mr-1" /> Add Staff</Button>} />

      <div className="space-y-2">
        {staff.map((s) => (
          <div key={s.user_id} className={`cc-list-row ${!s.active ? "opacity-60" : ""}`} data-testid={`staff-${s.user_id}`} onClick={() => setDetail(s)}>
            <div className="flex items-center gap-3">
              <Avatar className="h-11 w-11 border border-slate-200"><AvatarImage src={s.avatar} /><AvatarFallback className="bg-brand-light text-brand-dark font-bold">{s.name[0]}</AvatarFallback></Avatar>
              <div>
                <div className="font-semibold text-slate-800">{s.name} {!s.active && <span className="text-xs text-red-500">(inactive)</span>}</div>
                <div className="text-xs text-slate-500"><span className="capitalize">{s.role}</span> · {s.email}</div>
              </div>
            </div>
            <ChevronRight size={18} className="text-slate-300" />
          </div>
        ))}
      </div>

      {modal && <StaffModal onClose={() => setModal(false)} onSaved={(createdInvite) => { setModal(false); load(); if (createdInvite) setInvite(createdInvite); }} />}
      {detail && <StaffDetail staff={detail} canEdit={has("edit_staff")} onClose={() => setDetail(null)} onChanged={() => { setDetail(null); load(); }} />}
      {invite && <InviteLinkDialog invite={invite} onClose={() => setInvite(null)} />}
    </div>
  );
}

function StaffModal({ staff, onClose, onSaved }) {
  const [f, setF] = useState(staff || { name: "", email: "", phone: "", role: "staff", department: "Kitchen", permissions: [], active: true, site_access: "all", site_ids: [] });
  const [sites, setSites] = useState([]);
  const [presetApplied, setPresetApplied] = useState(false);
  useEffect(() => { api.get("/venues").then((r) => setSites(r.data)).catch(() => {}); }, []);
  // Onboarding default-selection: for a NEW normal (non-Bar) Staff invite, pre-tick operational
  // staff permissions (admin can untick before inviting). Bar Staff / other roles → no broad preset.
  // Backend authorisation remains the source of truth.
  useEffect(() => {
    if (staff) return; // never rewrite an existing user's permissions
    api.get(`/workforce/staff-permission-preset?role=${f.role}&department=${encodeURIComponent(f.department || "")}`)
      .then((r) => setF((prev) => ({ ...prev, permissions: r.data.preset })))
      .catch(() => {})
      .finally(() => setPresetApplied(true));
    // eslint-disable-next-line
  }, [f.role, f.department]);
  const togglePerm = (p) => setF({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] });
  const toggleSite = (id) => setF({ ...f, site_ids: (f.site_ids || []).includes(id) ? f.site_ids.filter((x) => x !== id) : [...(f.site_ids || []), id] });
  const save = async () => {
    if (!f.name || (!staff && !f.email)) return toast.error("Name and email required");
    if (staff) {
      await api.put(`/staff/${staff.user_id}`, f);
      toast.success("Staff updated");
      onSaved();
      return;
    }
    const { data } = await api.post("/staff", f);
    if (data.invite_link) {
      toast.success("Staff invitation created");
      onSaved({ email: f.email, link: data.invite_link });
    } else {
      toast.success("Staff added");
      onSaved();
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="staff-modal" className="max-w-lg">
        <DialogHeader><DialogTitle>{staff ? `Edit ${staff.name}` : "Add staff member"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name" className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="staff-role" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admin">Owner / Admin</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent></Select></div>
          </div>
          {!staff && f.role === "staff" && (
            <div><Label className="text-xs font-semibold text-slate-600">Department</Label>
              <Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}><SelectTrigger data-testid="staff-department" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["Kitchen", "Front of House", "Bar", "Cleaning", "Other"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
              <p className="text-[11px] text-slate-400 mt-1" data-testid="staff-preset-hint">
                {(f.department || "").toLowerCase().includes("bar")
                  ? "Bar staff: choose permissions manually below."
                  : "Normal staff permissions are pre-selected — untick anything before inviting."}</p>
            </div>
          )}
          {!staff && <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email" className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Mobile</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="staff-phone" className="mt-1.5" /></div>
          </div>}
          <div>
            <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-2">Site access</Label>
            <Select value={f.site_access} onValueChange={(v) => setF({ ...f, site_access: v })}><SelectTrigger data-testid="staff-site-access"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All sites in organisation</SelectItem><SelectItem value="selected">Selected sites only</SelectItem></SelectContent></Select>
            {f.site_access === "selected" && sites.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {sites.map((s) => (
                  <label key={s.venue_id} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid={`staff-site-${s.venue_id}`}>
                    <Checkbox checked={(f.site_ids || []).includes(s.venue_id)} onCheckedChange={() => toggleSite(s.venue_id)} className="h-4 w-4" /> {s.name}
                  </label>
                ))}
              </div>
            )}
            {f.site_access === "selected" && sites.length === 0 && <p className="text-xs text-slate-400 mt-1">No sites yet — add sites in Settings.</p>}
          </div>
          <div data-testid="role-permissions-help">
            <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-2"><ShieldCheck size={14} /> Permissions</Label>
            {f.role === "admin" && (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-xs text-emerald-900">
                Owner / Admin already includes every CaterCTRL permission. Nothing else needs to be selected.
              </div>
            )}
            {f.role === "manager" && (
              <div className="space-y-3">
                <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs text-slate-700">
                  <div className="font-semibold text-slate-800 mb-1">Manager permissions are automatic</div>
                  Managers already receive all day-to-day operational permissions. You do not need to tick every option. Only add either permission below if this manager should also have that extra authority.
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="perm-edit_staff">
                  <Checkbox checked={f.permissions.includes("edit_staff")} onCheckedChange={() => togglePerm("edit_staff")} className="h-4 w-4 mt-0.5" />
                  <span><span className="font-medium text-slate-700">Edit staff accounts</span><br />Invite, edit, deactivate and reactivate staff users.</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="perm-manage_settings">
                  <Checkbox checked={f.permissions.includes("manage_settings")} onCheckedChange={() => togglePerm("manage_settings")} className="h-4 w-4 mt-0.5" />
                  <span><span className="font-medium text-slate-700">Manage organisation settings</span><br />Change organisation-level settings normally reserved for an Owner / Admin.</span>
                </label>
              </div>
            )}
            {f.role === "staff" && (
              <>
                <p className="text-[11px] text-slate-400 mb-1">Staff permissions shown below are the permissions that will be added to their role defaults.</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {Object.entries(PERM_GROUPS).map(([group, perms]) => (
                    <div key={group} className="col-span-2 sm:col-span-1">
                      <div className="text-[11px] font-bold uppercase text-slate-400 mt-2">{group}</div>
                      {perms.map((p) => (
                        <label key={p} className="flex items-center gap-2 text-xs text-slate-600 py-0.5 cursor-pointer" data-testid={`perm-${p}`}>
                          <Checkbox checked={f.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} className="h-4 w-4" /> {p.replace(/_/g, " ")}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-staff" className="bg-brand hover:bg-brand-hover">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteLinkDialog({ invite, onClose }) {
  const [showLink, setShowLink] = useState(false);
  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(invite.link);
      toast.success("Invitation link copied");
    } catch {
      toast.error("Could not copy the invitation link");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="staff-invite-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Invitation ready</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            The account for <span className="font-medium text-slate-800">{invite.email}</span> has been created.
          </p>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            Automatic invitation email delivery is not enabled yet. Copy this single-use link and send it securely to the staff member. It expires after 72 hours.
          </div>
          <div className="flex gap-2">
            <Input
              aria-label="Invitation link"
              readOnly
              value={showLink ? invite.link : "••••••••••••••••••••••••••••••••"}
              className="font-mono text-xs"
              data-testid="staff-invite-link"
            />
            <Button type="button" variant="outline" size="icon" onClick={() => setShowLink((v) => !v)} aria-label={showLink ? "Hide invitation link" : "Show invitation link"}>
              {showLink ? <EyeOff size={16} /> : <Eye size={16} />}
            </Button>
            <Button type="button" onClick={copyInvite} className="bg-brand hover:bg-brand-hover" data-testid="copy-staff-invite">
              <Copy size={16} className="mr-1" /> Copy
            </Button>
          </div>
        </div>
        <DialogFooter><Button onClick={onClose}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffDetail({ staff, onClose, onChanged, canEdit }) {
  const [full, setFull] = useState(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => { api.get(`/staff/${staff.user_id}`).then((r) => setFull(r.data)); }, [staff]);
  if (editing) return <StaffModal staff={staff} onClose={() => setEditing(false)} onSaved={onChanged} />;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="staff-detail" className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-3">
          <Avatar className="h-10 w-10"><AvatarFallback className="bg-brand-light text-brand-dark font-bold">{staff.name[0]}</AvatarFallback></Avatar>
          <div>{staff.name}<div className="text-xs font-normal text-slate-400 capitalize">{staff.role}</div></div>
        </DialogTitle></DialogHeader>
        <div className="text-sm space-y-1 text-slate-600">
          <div>{staff.email}{staff.phone && ` · ${staff.phone}`}</div>
          <div className="flex flex-wrap gap-1 mt-2">{(full?.permissions_effective || []).map((p) => <span key={p} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{p.replace(/_/g, " ")}</span>)}</div>
        </div>
        <div className="mt-2">
          <div className="text-xs font-bold uppercase text-slate-400 mb-2">Recent activity</div>
          <div className="max-h-52 overflow-y-auto space-y-1.5">
            {(full?.recent_activity || []).length === 0 && <div className="text-xs text-slate-400">No activity recorded.</div>}
            {(full?.recent_activity || []).map((a) => <div key={a.log_id} className="text-xs text-slate-600 border-l-2 border-brand pl-2">{a.summary}<span className="text-slate-400"> · {fmtDateTime(a.timestamp)}</span></div>)}
          </div>
        </div>
        {canEdit && <DialogFooter className="flex-row gap-2">
          <Button variant="outline" data-testid="edit-staff" onClick={() => setEditing(true)}><UserCog size={16} className="mr-1" /> Edit</Button>
          {staff.active
            ? <Button variant="outline" className="text-red-600 border-red-200" data-testid="deactivate-staff" onClick={async () => { await api.post(`/staff/${staff.user_id}/deactivate`); toast.success("Deactivated"); onChanged(); }}>Deactivate</Button>
            : <Button variant="outline" className="text-brand-dark" data-testid="activate-staff" onClick={async () => { await api.post(`/staff/${staff.user_id}/activate`); toast.success("Reactivated"); onChanged(); }}>Reactivate</Button>}
        </DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
